import type {
  CreateSaleQuoteRequest,
  SaleQuoteResponse,
  SaleTenderRequest,
} from "./generated-client";
import { isPosCheckoutPaymentMethod } from "../constants/payment-methods";

export interface SaleCartLineInput {
  lineId: string;
  productId: string;
  /** Server-owned pack definition selected by the operator. */
  packId?: string;
  /** Quantity expressed in the selected pack, or in base units without packId. */
  quantity: number;
  /** Explicit non-scheme free quantity in the same document UOM. */
  freeQuantity?: number;
  grossRupees: number;
  discountRupees: number;
  preferredLotId?: string;
  lotOverrideReason?: string;
  prescribedProductId?: string;
  prescriptionLineId?: string;
  substitutionReason?: string;
  substitutionConsent?: boolean;
  minimumResidualShelfLifeDays?: number;
  coldChainRequired?: boolean;
  controlledStorageRequired?: boolean;
}

export interface SaleQuoteInput {
  customerId?: string;
  interstate?: boolean;
  billDiscountRupees?: number;
  context?: CreateSaleQuoteRequest["context"];
  lines: SaleCartLineInput[];
}

export interface OfferedTender {
  method: string;
  amountRupees: number;
  reference?: string;
}

function decimalQuantity(value: number, field: string): string {
  if (!Number.isFinite(value) || value <= 0) {
    throw new Error(`${field} must be a positive finite quantity`);
  }
  return value.toFixed(6);
}

function discountBasisPoints(grossRupees: number, discountRupees: number) {
  if (!Number.isFinite(grossRupees) || grossRupees <= 0) return 0;
  if (!Number.isFinite(discountRupees) || discountRupees <= 0) return 0;
  return Math.min(
    10_000,
    Math.max(0, Math.round((discountRupees / grossRupees) * 10_000)),
  );
}

export function buildSaleQuoteRequest(
  input: SaleQuoteInput,
): CreateSaleQuoteRequest {
  if (!input.lines.length) throw new Error("A sale requires at least one line");
  return {
    customerId: input.customerId,
    interstate: input.interstate ?? false,
    ...(input.billDiscountRupees && input.billDiscountRupees > 0
      ? { billDiscountPaise: Math.round(input.billDiscountRupees * 100) }
      : {}),
    ...(input.context ? { context: input.context } : {}),
    items: input.lines.map((line) => {
      const discountBps = discountBasisPoints(
        line.grossRupees,
        line.discountRupees,
      );
      const freeQuantity = Math.max(0, line.freeQuantity ?? 0);
      return {
        lineId: line.lineId,
        productId: line.productId,
        ...(line.packId ? { packId: line.packId } : {}),
        quantity: decimalQuantity(line.quantity, "quantity"),
        ...(freeQuantity > 0 ? { freeQuantity: freeQuantity.toFixed(6) } : {}),
        ...(discountBps > 0 ? { discountBps } : {}),
        ...(line.preferredLotId ? { preferredLotId: line.preferredLotId } : {}),
        ...(line.lotOverrideReason
          ? { lotOverrideReason: line.lotOverrideReason }
          : {}),
        ...(line.prescribedProductId
          ? { prescribedProductId: line.prescribedProductId }
          : {}),
        ...(line.prescriptionLineId
          ? { prescriptionLineId: line.prescriptionLineId }
          : {}),
        ...(line.substitutionReason
          ? { substitutionReason: line.substitutionReason }
          : {}),
        ...(line.substitutionConsent !== undefined
          ? { substitutionConsent: line.substitutionConsent }
          : {}),
        ...(line.minimumResidualShelfLifeDays !== undefined
          ? {
              minimumResidualShelfLifeDays: line.minimumResidualShelfLifeDays,
            }
          : {}),
        ...(line.coldChainRequired !== undefined
          ? { coldChainRequired: line.coldChainRequired }
          : {}),
        ...(line.controlledStorageRequired !== undefined
          ? { controlledStorageRequired: line.controlledStorageRequired }
          : {}),
      };
    }),
  };
}

export function allocateSaleTenders(
  payablePaise: number,
  offered: OfferedTender[],
): SaleTenderRequest[] {
  if (!Number.isInteger(payablePaise) || payablePaise <= 0) {
    throw new Error("Sale payable must be a positive paise amount");
  }
  let remaining = payablePaise;
  const tenders: SaleTenderRequest[] = [];
  for (const [index, line] of offered.entries()) {
    if (!isPosCheckoutPaymentMethod(line.method)) {
      throw new Error(`Unsupported tender method ${line.method}`);
    }
    const rawPaise = line.amountRupees * 100;
    const roundedPaise = Math.round(rawPaise);
    if (
      !Number.isFinite(line.amountRupees) ||
      line.amountRupees < 0 ||
      !Number.isSafeInteger(roundedPaise) ||
      Math.abs(rawPaise - roundedPaise) > 1e-6
    )
      throw new Error(`Tender ${index + 1} must be an exact paise amount`);
    const offeredPaise = roundedPaise;
    if (offeredPaise > 0 && remaining === 0)
      throw new Error("Tender allocations exceed the amount due");
    if (line.method !== "CASH" && offeredPaise > remaining)
      throw new Error("Non-cash tenders cannot produce change");
    const amountPaise = Math.min(remaining, offeredPaise);
    if (amountPaise > 0) {
      tenders.push({
        method: line.method as SaleTenderRequest["method"],
        amountPaise,
        ...(line.method === "CASH" && offeredPaise > amountPaise
          ? { tenderedPaise: offeredPaise }
          : {}),
        ...(line.reference ? { reference: line.reference } : {}),
      });
      remaining -= amountPaise;
    }
  }
  if (remaining !== 0) {
    throw new Error(`Tender allocations are short by ${remaining} paise`);
  }
  return tenders;
}

export function requiredSaleComplianceFields(
  quote: Pick<SaleQuoteResponse, "lines">,
): string[] {
  return [...new Set(quote.lines.flatMap((line) => line.complianceFields))];
}

export function collectSaleCompliance(
  requiredFields: string[],
  values: Record<string, string | null | undefined>,
): { compliance: Record<string, string>; missing: string[] } {
  const compliance: Record<string, string> = {};
  for (const [field, rawValue] of Object.entries(values)) {
    const value = rawValue?.trim();
    if (value) compliance[field] = value;
  }
  const missing = requiredFields.filter((field) => !compliance[field]);
  return { compliance, missing };
}

export function calculateSaleReturnRefundPaise(
  lineTotalPaise: number,
  originalPaidQuantity: number,
  returnPaidQuantity: number,
  settlementAdjustmentPaise = 0,
  previouslyReturnedPaidQuantity = 0,
) {
  if (!Number.isSafeInteger(lineTotalPaise) || lineTotalPaise < 0) {
    throw new Error("Line total must be a non-negative safe paise amount");
  }
  if (!Number.isSafeInteger(settlementAdjustmentPaise)) {
    throw new Error("Settlement adjustment must be a safe paise amount");
  }
  const totalMicros = Math.round(originalPaidQuantity * 1_000_000);
  const returnMicros = Math.round(returnPaidQuantity * 1_000_000);
  const returnedBeforeMicros = Math.round(
    previouslyReturnedPaidQuantity * 1_000_000,
  );
  if (
    totalMicros <= 0 ||
    returnMicros < 0 ||
    returnedBeforeMicros < 0 ||
    returnedBeforeMicros + returnMicros > totalMicros
  ) {
    throw new Error("Return quantity must be within the sold paid quantity");
  }
  if (returnMicros === 0) return 0;

  const roundHalfUpRatio = (
    amountPaise: number,
    numerator: number,
    denominator: number,
  ) =>
    Number(
      (BigInt(amountPaise) * BigInt(numerator) + BigInt(denominator) / 2n) /
        BigInt(denominator),
    );
  const proportionalSignedPaise = (numerator: number) => {
    if (numerator <= 0 || settlementAdjustmentPaise === 0) return 0;
    if (numerator >= totalMicros) return settlementAdjustmentPaise;
    const sign = settlementAdjustmentPaise < 0 ? -1 : 1;
    return (
      sign *
      roundHalfUpRatio(
        Math.abs(settlementAdjustmentPaise),
        numerator,
        totalMicros,
      )
    );
  };

  const baseRefundPaise = roundHalfUpRatio(
    lineTotalPaise,
    returnMicros,
    totalMicros,
  );
  const roundOffPaise =
    proportionalSignedPaise(returnedBeforeMicros + returnMicros) -
    proportionalSignedPaise(returnedBeforeMicros);
  return baseRefundPaise + roundOffPaise;
}
